import {
  Activity,
  BarChart3,
  Calendar,
  DollarSign,
  Globe,
  Phone,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";

export default function ForInvestors() {
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    fetchHeroBackgrounds("investors").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);

  /** 🔁 SWITCH THIS TO TRUE WHEN YOU HAVE REAL METRICS */
  const hasRealMetrics = true;

  /** =====================
   *  LAUNCH READINESS
   *  ===================== */
  const launchMetrics = [
    {
      change: "Live Soon",
      icon: <Users className="text-primary" size={24} />,
      label: "User Onboarding",
      value: "Pre-Launch",
    },
    {
      change: "In Progress",
      icon: <DollarSign className="text-success" size={24} />,
      label: "Revenue Model",
      value: "Configured",
    },
    {
      change: "Pilot Phase",
      icon: <Globe className="text-info" size={24} />,
      label: "Service Areas",
      value: "Initial Cities",
    },
    {
      change: "Growing",
      icon: <Activity className="text-warning" size={24} />,
      label: "Partner Network",
      value: "Early Drivers",
    },
  ];

  /** =====================
   *  MARKET OPPORTUNITY
   *  ===================== */
  const marketOpportunity = [
    {
      description: "Growing urbanization driving demand for mobility solutions",
      growth: "25% CAGR",
      size: "R15B",
      title: "Ride-Hailing Market",
    },
    {
      description: "E-commerce and convenience culture expansion",
      growth: "30% CAGR",
      size: "R8B",
      title: "Food Delivery",
    },
    {
      description: "B2B logistics digitization opportunity",
      growth: "20% CAGR",
      size: "R50B",
      title: "Logistics & Freight",
    },
  ];

  /** =====================
   *  COMPETITIVE ADVANTAGES
   *  ===================== */
  const competitiveAdvantages = [
    {
      description:
        "Deep understanding of African markets, local partnerships, and regulatory landscape",
      icon: <Target className="text-primary" size={32} />,
      title: "Local Market Focus",
    },
    {
      description:
        "Advanced algorithms, real-time optimization, and scalable platform architecture",
      icon: <Zap className="text-warning" size={32} />,
      title: "Technology Leadership",
    },
    {
      description:
        "Higher driver retention and satisfaction leading to better service quality",
      icon: <Users className="text-success" size={32} />,
      title: "Partner-Centric Approach",
    },
    {
      description:
        "Integrated ecosystem spanning rides, delivery, and logistics",
      icon: <Globe className="text-info" size={32} />,
      title: "Multi-Service Platform",
    },
  ];

  /** =====================
   *  BUSINESS MODEL
   *  ===================== */
  const revenueStreams = [
    { percentage: 60, stream: "Ride Commission" },
    { percentage: 25, stream: "Delivery Services" },
    { percentage: 10, stream: "Subscriptions" },
    { percentage: 5, stream: "Advertising & Partnerships" },
  ];

  /** =====================
   *  FUNDING ROADMAP
   *  ===================== */
  const fundingRoadmap = [
    {
      amount: "Seed Round",
      date: "2025",
      investors: "Open to strategic and early-stage partners",
      round: "Seed",
      status: "Planned",
    },
    {
      amount: "Series A",
      date: "2026",
      investors: "Institutional & Venture Capital",
      round: "Series A",
      status: "Target",
    },
  ];

  if (
    typeof document !== "undefined" &&
    !document.getElementById("hero-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "hero-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 2s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* =====================
            HERO SECTION
        ===================== */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-20">
            {heroLoading ? (
              <div className="relative h-full w-full overflow-hidden bg-base-200">
                <div
                  className="animate-shimmer absolute inset-0 bg-gradient-to-r from-base-200 via-base-100 to-base-200"
                  style={{ backgroundSize: "200% 100%" }}
                />
              </div>
            ) : heroBackground?.imageUrl ? (
              <img
                alt={heroBackground?.imageAlt || "Hero background"}
                className="h-full w-full object-cover"
                src={heroBackground.imageUrl}
              />
            ) : null}
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="max-w-4xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary-content/20 px-4 py-2">
                <TrendingUp size={20} />
                <span className="font-medium">
                  Launching · Early Investor Access
                </span>
              </div>

              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                Investing in Africa’s{" "}
                <span className="text-primary">mobility revolution</span>
              </h1>

              <p className="mb-8 text-xl leading-relaxed opacity-90">
                Join us as we build a scalable mobility and logistics platform
                designed for African markets. Explore our opportunity, business
                model, and growth roadmap.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row">
                <a className="btn btn-primary btn-lg" href="#business-model">
                  <Zap size={18} /> Our Business Model
                </a>
                <a className="btn btn-outline btn-lg" href="/contact-us">
                  <Phone size={18} /> Contact Us
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* =====================
            PLATFORM READINESS
        ===================== */}
        {!hasRealMetrics && (
          <section className="py-16">
            <div className="mx-auto max-w-7xl px-4">
              <div className="mb-12 text-center">
                <h2 className="mb-4 text-4xl font-bold">Platform Readiness</h2>
                <p className="text-xl text-base-content/70">
                  Launch status across our core business pillars
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {launchMetrics.map((metric, index) => (
                  <div
                    className="stat rounded-lg bg-base-100 shadow-lg"
                    key={index}
                  >
                    <div className="stat-figure">{metric.icon}</div>
                    <div className="stat-title">{metric.label}</div>
                    <div className="stat-value">{metric.value}</div>
                    <div className="stat-desc text-success">
                      <span className="font-semibold">{metric.change}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* =====================
            MARKET OPPORTUNITY
        ===================== */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Market Opportunity</h2>
              <p className="text-xl text-base-content/70">
                Large and fast-growing mobility and logistics sectors
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {marketOpportunity.map((market, index) => (
                <div className="card bg-base-200 shadow-lg" key={index}>
                  <div className="card-body text-center">
                    <div className="mb-2 text-3xl font-bold text-primary">
                      {market.size}
                    </div>
                    <h3 className="card-title mb-2 justify-center">
                      {market.title}
                    </h3>
                    <div className="badge badge-success mb-4">
                      {market.growth}
                    </div>
                    <p className="text-base-content/70">{market.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================
            BUSINESS MODEL
        ===================== */}
        <section className="py-16" id="business-model">
          <div className="mx-auto max-w-7xl px-4">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <h2 className="mb-6 text-4xl font-bold">Business Model</h2>
                <p className="mb-8 text-lg text-base-content/70">
                  A diversified revenue structure designed for long-term growth
                  and profitability.
                </p>

                <div className="space-y-4">
                  {revenueStreams.map((stream, index) => (
                    <div
                      className="flex items-center justify-between rounded-lg bg-base-100 p-4"
                      key={index}
                    >
                      <div className="flex-1">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="font-semibold">{stream.stream}</span>
                          <span className="font-bold text-primary">
                            Planned
                          </span>
                        </div>
                        <progress
                          className="progress progress-primary w-full"
                          max="100"
                          value={stream.percentage}
                        />
                      </div>
                      <div className="ml-4 text-sm font-semibold">
                        {stream.percentage}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="card bg-base-100 shadow-xl">
                  <div className="card-body">
                    <h3 className="card-title mb-4">Growth Strategy</h3>
                    <div className="flex h-64 items-center justify-center rounded-lg bg-base-200">
                      <div className="text-center">
                        <BarChart3
                          className="mx-auto mb-4 text-primary"
                          size={48}
                        />
                        <p className="text-base-content/70">
                          User acquisition, partnerships, and geographic
                          expansion
                        </p>
                        <div className="mt-4 text-2xl font-bold text-success">
                          Scalable Model
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================
            COMPETITIVE ADVANTAGES
        ===================== */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">
                Competitive Advantages
              </h2>
              <p className="text-xl text-base-content/70">
                What differentiates us in the African mobility ecosystem
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {competitiveAdvantages.map((advantage, index) => (
                <div className="card bg-base-200 shadow-lg" key={index}>
                  <div className="card-body text-center">
                    <div className="mx-auto mb-4">{advantage.icon}</div>
                    <h3 className="card-title mb-2 justify-center">
                      {advantage.title}
                    </h3>
                    <p className="text-base-content/70">
                      {advantage.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================
            FUNDING ROADMAP
        ===================== */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Funding Roadmap</h2>
              <p className="text-xl text-base-content/70">
                Our capital strategy to scale operations and market reach
              </p>
            </div>

            <div className="space-y-6">
              {fundingRoadmap.map((round, index) => (
                <div className="card bg-base-100 shadow-lg" key={index}>
                  <div className="card-body">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex-1">
                        <div className="mb-2 flex items-center gap-4">
                          <h3 className="text-2xl font-bold">{round.round}</h3>
                          <span className="badge badge-warning">
                            {round.status}
                          </span>
                        </div>
                        <div className="mb-2 text-3xl font-bold text-primary">
                          {round.amount}
                        </div>
                        <p className="text-base-content/70">
                          {round.investors}
                        </p>
                      </div>

                      <div className="mt-4 lg:ml-6 lg:mt-0">
                        <div className="flex items-center gap-2 text-base-content/70">
                          <Calendar size={16} />
                          <span>{round.date}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <div className="card inline-block border border-primary/20 bg-primary/10">
                <div className="card-body text-center">
                  <h3 className="mb-2 text-2xl font-bold text-primary">
                    Fundraising Stage
                  </h3>
                  <div className="text-4xl font-bold">Pre-Seed / Seed</div>
                  <p className="mt-2 text-base-content/70">
                    Preparing for initial investment round
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================
            LEADERSHIP
        ===================== */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Leadership</h2>
              <p className="text-xl text-base-content/70">
                Founder-led team focused on technology, operations, and growth
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="card bg-base-200 shadow-lg">
                <div className="card-body text-center">
                  <h3 className="card-title justify-center">Founder</h3>
                  <p className="font-semibold text-primary">
                    Product & Engineering
                  </p>
                  <p className="text-sm text-base-content/70">
                    Building scalable mobility solutions for African markets
                  </p>
                </div>
              </div>

              <div className="card bg-base-200 shadow-lg">
                <div className="card-body text-center">
                  <h3 className="card-title justify-center">Operations</h3>
                  <p className="font-semibold text-primary">
                    Partnerships & Growth
                  </p>
                  <p className="text-sm text-base-content/70">
                    Driving onboarding, partnerships, and city expansion
                  </p>
                </div>
              </div>

              <div className="card bg-base-200 shadow-lg">
                <div className="card-body text-center">
                  <h3 className="card-title justify-center">Advisors</h3>
                  <p className="font-semibold text-primary">
                    Industry & Technology
                  </p>
                  <p className="text-sm text-base-content/70">
                    Strategic guidance across mobility and digital platforms
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
